/**
 * Vehicle "buckets" that represent unassigned / holding states rather than a
 * real route. Orders sitting in these buckets are not optimized yet.
 */
export const UNASSIGNED_VEHICLES = ['Unfit Stops', 'ReadyToRoute', 'PreRoute']

export function isUnassignedVehicle(vehicle: string): boolean {
  return UNASSIGNED_VEHICLES.includes(vehicle)
}

/**
 * A window whose year is 1999 or 2000 is a sentinel meaning "no real date has
 * been assigned to this order yet".
 */
export function isUnassignedDate(windowStr: string): boolean {
  const year = windowStr.split('-')[0]
  return year === '1999' || year === '2000'
}

export interface OrderWindowSource {
  startWindow: string;
  endWindow: string;
  vehicle: string;
}

export interface ResolvedWindowInputs {
  startInput: string;
  endInput: string;
}

/**
 * Resolve the moment.tz input strings for an order's start/end windows when
 * pushing data back to Firestore. The caller wraps each result with
 * `moment.tz(input, timezone).toDate()`.
 *
 * Behaviour by case:
 *  - order already has a real (non-sentinel) window -> preserved EXACTLY
 *    (date + time), regardless of vehicle. Assigning a vehicle (incl. via the
 *    draw-shape tool) must never change the delivery window.
 *  - undated/sentinel order assigned to a real vehicle -> scheduled onto the
 *    selected date, keeping the order's time-of-day.
 *  - undated/sentinel order still in a holding bucket -> the 2000-01-01 sentinel.
 */
export function resolveWindowInputs(
  order: OrderWindowSource,
  selectedDate: string,
): ResolvedWindowInputs {
  // A real window is the customer's delivery commitment — never rewrite it.
  if (!isUnassignedDate(order.startWindow)) {
    return {
      startInput: order.startWindow.split('.')[0],
      endInput: order.endWindow.split('.')[0],
    }
  }

  // No real date yet: schedule onto the selected date when assigned to a real
  // vehicle, otherwise keep the sentinel while it sits in a holding bucket.
  if (!isUnassignedVehicle(order.vehicle)) {
    return {
      startInput: `${selectedDate}T${order.startWindow.split('T')[1].split('.')[0]}`,
      endInput: `${selectedDate}T${order.endWindow.split('T')[1].split('.')[0]}`,
    }
  }

  return { startInput: '2000-01-01T00:00:00', endInput: '2000-01-01T00:00:00' }
}
